import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { TrendingUp, TrendingDown, Minus, Repeat, Zap, Award, Info } from "lucide-react";

/**
 * ConfrontoStagioniPanel — pannello "AI insights" del Global Series A Rooster.
 * Analizza la carriera del giocatore e produce badge automatici:
 *   - trend valore (crescita/stabile/calo)
 *   - variazioni di ruolo
 *   - picchi/minimi storici
 *   - stabilità di club
 *
 * Iter 55 (Feb 2026). Non richiede endpoint dedicati: usa `player.career`.
 */

// Mantra role → color (aligned con LeagueRosa)
const MANTRA_COLOR = {
  Por: "#f59e0b", P: "#f59e0b",
  Dc: "#10b981", Dd: "#14b8a6", Ds: "#0d9488",
  B: "#22c55e", E: "#84cc16",
  M: "#3b82f6", C: "#6366f1",
  T: "#8b5cf6",
  W: "#d946ef",
  A: "#f97316", Pc: "#f43f5e",
};

function roleColor(role) {
  if (!role) return "#64748b";
  // Normalize: capitalize first letter, rest lower
  const key = role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  return MANTRA_COLOR[key] || MANTRA_COLOR[role.toUpperCase()] || "#64748b";
}

function computeInsights(career) {
  if (!career || career.length === 0) return null;
  const sorted = [...career].sort((a, b) => (a.season_num || 0) - (b.season_num || 0));

  const values = sorted.map(c => Number(c.final_value) || 0).filter(v => v > 0);
  const qti = sorted.map(c => Number(c.initial_quotation) || 0).filter(v => v > 0);

  // Trend: slope linear regression on values by season
  let slope = 0;
  if (values.length >= 2) {
    const n = values.length;
    const xMean = (n - 1) / 2;
    const yMean = values.reduce((a, b) => a + b, 0) / n;
    let num = 0, den = 0;
    values.forEach((y, i) => {
      num += (i - xMean) * (y - yMean);
      den += (i - xMean) ** 2;
    });
    slope = den > 0 ? num / den : 0;
  }

  const trend =
    slope > 2 ? { label: "Crescita costante", icon: TrendingUp, color: "emerald", detail: `+${slope.toFixed(1)}cr/stagione` } :
    slope < -2 ? { label: "In calo", icon: TrendingDown, color: "rose", detail: `${slope.toFixed(1)}cr/stagione` } :
    slope > 0.5 ? { label: "Trend positivo", icon: TrendingUp, color: "amber", detail: `+${slope.toFixed(1)}cr/stagione` } :
    slope < -0.5 ? { label: "Trend negativo", icon: TrendingDown, color: "orange", detail: `${slope.toFixed(1)}cr/stagione` } :
    { label: "Valore stabile", icon: Minus, color: "slate", detail: "Poche variazioni" };

  // Peak/min
  const peakIdx = values.length ? values.indexOf(Math.max(...values)) : -1;
  const minIdx = values.length ? values.indexOf(Math.min(...values)) : -1;
  const peak = peakIdx >= 0 ? { season: sorted[peakIdx].season, value: values[peakIdx] } : null;
  const trough = minIdx >= 0 && minIdx !== peakIdx ? { season: sorted[minIdx].season, value: values[minIdx] } : null;

  // Role changes
  const roles = sorted.map(c => (c.ruolo || "").toString().trim()).filter(Boolean);
  const roleChanges = [];
  for (let i = 1; i < roles.length; i++) {
    if (roles[i] !== roles[i - 1]) {
      roleChanges.push({ from: roles[i - 1], to: roles[i], season: sorted[i].season });
    }
  }

  // Club stability
  const uniqueClubs = new Set(sorted.map(c => c.club_id).filter(Boolean));
  const clubStability =
    sorted.length >= 3 && uniqueClubs.size === 1 ? "loyal" :
    uniqueClubs.size / Math.max(1, sorted.length) < 0.5 ? "stable" :
    "mobile";

  // AI-style verdict
  const investmentVerdict = (() => {
    if (values.length < 2) return null;
    if (slope > 2 && uniqueClubs.size <= 2) return {
      tone: "buy",
      msg: "Investimento a lungo termine solido: cresce di valore in modo consistente.",
    };
    if (slope > 0.5) return {
      tone: "hold",
      msg: "Buon investimento di continuità: valore in leggera crescita.",
    };
    if (slope < -2) return {
      tone: "sell",
      msg: "Valore in calo: valuta cessione o rinnovo prudente.",
    };
    return {
      tone: "neutral",
      msg: "Valore stabile: buon comprimario a costo contenuto.",
    };
  })();

  return {
    trend,
    peak,
    trough,
    roleChanges,
    clubStability,
    uniqueClubs: uniqueClubs.size,
    investmentVerdict,
    seasons: sorted.length,
    avgValue: values.length ? Math.round(values.reduce((a, b) => a + b, 0) / values.length) : null,
    sortedCareer: sorted,
  };
}

const TONE_STYLES = {
  buy: "border-emerald-500/50 bg-emerald-500/10 text-emerald-200",
  hold: "border-amber-500/50 bg-amber-500/10 text-amber-200",
  sell: "border-rose-500/50 bg-rose-500/10 text-rose-200",
  neutral: "border-slate-700 bg-slate-800/60 text-slate-300",
};

const TREND_STYLES = {
  emerald: "border-emerald-500/50 bg-emerald-500/10 text-emerald-200",
  amber: "border-amber-500/50 bg-amber-500/10 text-amber-200",
  orange: "border-orange-500/50 bg-orange-500/10 text-orange-200",
  rose: "border-rose-500/50 bg-rose-500/10 text-rose-200",
  slate: "border-slate-700 bg-slate-800/60 text-slate-300",
};

export default function ConfrontoStagioniPanel({ career }) {
  const insights = useMemo(() => computeInsights(career), [career]);

  if (!insights) return null;
  const { trend, peak, trough, roleChanges, clubStability, uniqueClubs,
          investmentVerdict, seasons, avgValue, sortedCareer } = insights;

  const TrendIcon = trend.icon;

  return (
    <Card className="bg-slate-900/60 border-slate-800" data-testid="confronto-stagioni-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-cyan-400" />
          Confronto Stagioni
          <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 ml-2">
            AI insights
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* AI verdict */}
        {investmentVerdict && (
          <div className={`rounded-xl border p-3 ${TONE_STYLES[investmentVerdict.tone]}`} data-testid="ai-verdict">
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-80 mb-0.5">
              Consiglio AI
            </div>
            <div className="text-sm font-semibold">{investmentVerdict.msg}</div>
          </div>
        )}

        {/* Badge row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div className={`rounded-lg border p-2 ${TREND_STYLES[trend.color]}`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold opacity-80">
              <TrendIcon size={12} /> Trend
            </div>
            <div className="text-sm font-black mt-0.5">{trend.label}</div>
            <div className="text-[10px] opacity-70">{trend.detail}</div>
          </div>

          <div className="rounded-lg border border-slate-700 bg-slate-800/60 p-2">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500">
              <Award size={12} /> Picco
            </div>
            <div className="text-sm font-black text-amber-300 mt-0.5">
              {peak ? `${peak.value}cr` : "—"}
            </div>
            <div className="text-[10px] text-slate-400">{peak?.season || "—"}</div>
          </div>

          <div className="rounded-lg border border-slate-700 bg-slate-800/60 p-2">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500">
              <Repeat size={12} /> Ruoli
            </div>
            <div className="text-sm font-black text-slate-200 mt-0.5">
              {roleChanges.length === 0 ? "Stabile" : `${roleChanges.length} cambi`}
            </div>
            <div className="text-[10px] text-slate-400">
              {roleChanges.length === 0 ? "Sempre stesso ruolo" : roleChanges.slice(0, 1).map(r => `${r.from} → ${r.to}`)}
            </div>
          </div>

          <div className="rounded-lg border border-slate-700 bg-slate-800/60 p-2">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500">
              <Info size={12} /> Club
            </div>
            <div className="text-sm font-black text-slate-200 mt-0.5">
              {uniqueClubs} club
            </div>
            <div className="text-[10px] text-slate-400">
              {clubStability === "loyal" ? "Fedele" : clubStability === "stable" ? "Stabile" : "Mobile"}
            </div>
          </div>
        </div>

        {/* Role timeline */}
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">
            Ruoli per stagione
          </div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="role-timeline">
            {sortedCareer.map((c, i) => {
              const color = roleColor(c.ruolo);
              const prev = i > 0 ? sortedCareer[i - 1].ruolo : null;
              const changed = prev && prev !== c.ruolo;
              return (
                <div key={i} className="flex flex-col items-center gap-1">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-black text-slate-950 border-2 ${changed ? "ring-2 ring-amber-400" : ""}`}
                    style={{ background: color, borderColor: color }}
                    title={`${c.season}: ${c.ruolo}${changed ? ` (cambio da ${prev})` : ""}`}
                  >
                    {(c.ruolo || "?").slice(0, 2)}
                  </div>
                  <div className="text-[9px] font-mono text-slate-500">{c.season}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Additional stats */}
        <div className="grid grid-cols-3 gap-2 text-center pt-2 border-t border-slate-800">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Stagioni</div>
            <div className="text-lg font-black text-slate-100">{seasons}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Valore medio</div>
            <div className="text-lg font-black text-amber-300">{avgValue ? `${avgValue}cr` : "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Minimo</div>
            <div className="text-lg font-black text-slate-400">{trough ? `${trough.value}cr` : "—"}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
